import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  closestCenter,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { format, startOfWeek, addDays, isToday, isSameDay } from 'date-fns';
import { sk } from 'date-fns/locale';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Task } from '@shared/schema';
import { CalendarDayColumn } from './calendar-day-column';
import { TaskCard } from './task-card';

interface WeeklyCalendarProps {
  tasks: Task[];
  onTaskClick?: (taskId: number) => void;
}

type TaskWithDay = Task & { day: number };

export function WeeklyCalendar({ tasks, onTaskClick }: WeeklyCalendarProps) {
  const today = new Date();
  const startOfCurrentWeek = startOfWeek(today, { weekStartsOn: 1 }); // Týždeň začína pondelkom
  
  const [activeId, setActiveId] = useState<number | null>(null);
  const [taskDays, setTaskDays] = useState<Record<number, number>>({});
  const [weeklyTasks, setWeeklyTasks] = useState<TaskWithDay[]>([]);
  
  // Senzory pre drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Minimálna vzdialenosť pre aktiváciu drag
      },
    })
  );
  
  // Pri inicializácii alebo zmene úloh nastavíme počiatočné rozloženie
  useEffect(() => {
    // Načítame uložené rozloženie z localStorage (ak existuje)
    const savedLayout = localStorage.getItem('taskCalendarLayout');
    let savedTaskDays: Record<number, number> = {};
    
    if (savedLayout) {
      try {
        savedTaskDays = JSON.parse(savedLayout);
      } catch (e) {
        // Failed to parse saved calendar layout - using defaults
      }
    }
    
    // Pre úlohy, ktoré nemajú uložené umiestnenie, určíme deň podľa dueDate
    const newTaskDays = { ...savedTaskDays };
    
    // Pripravíme úlohy s pridaným atribútom dňa
    const tasksWithDays = tasks.map(task => {
      // Normalizujeme dueDate - podporujeme oba formáty (dueDate aj due_date)
      const dueDate = task.dueDate || (task as any).due_date || null;
      
      // Ak nemáme uložený deň, skúsime použiť dueDate alebo defaultne pondelok (0)
      if (newTaskDays[task.id] === undefined) {
        if (dueDate) {
          try {
            const dueDateObj = dueDate instanceof Date ? dueDate : new Date(dueDate);
            if (!isNaN(dueDateObj.getTime())) {
              const dayDiff = Math.floor((dueDateObj.getTime() - startOfCurrentWeek.getTime()) / (1000 * 60 * 60 * 24));
              
              // Ak je dueDate v tomto týždni, použijeme ten deň, inak pondelok
              if (dayDiff >= 0 && dayDiff < 7) {
                newTaskDays[task.id] = dayDiff;
              } else {
                newTaskDays[task.id] = 0; // Pondelok ako default
              }
            } else {
              newTaskDays[task.id] = 0; // Pondelok ako default ak dueDate nie je platný
            }
          } catch (e) {
            newTaskDays[task.id] = 0; // Pondelok ako default ak je chyba
          }
        } else {
          newTaskDays[task.id] = 0; // Pondelok ako default
        }
      }
      
      return {
        ...task,
        dueDate: dueDate ? (typeof dueDate === 'string' ? dueDate : dueDate.toISOString()) : null,
        day: newTaskDays[task.id]
      };
    });
    
    setTaskDays(newTaskDays);
    setWeeklyTasks(tasksWithDays);
    
    // Uložíme aktualizované rozloženie do localStorage
    localStorage.setItem('taskCalendarLayout', JSON.stringify(newTaskDays));
  }, [tasks]);
  
  // Vytvoríme dni týždňa
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(startOfCurrentWeek, i);
    return {
      date,
      label: format(date, 'EEEE', { locale: sk }),
      dateString: format(date, 'd. MMMM', { locale: sk }),
      isToday: isToday(date)
    };
  });
  
  // Handler pre začiatok drag
  function handleDragStart(event: DragStartEvent) {
    const { active } = event;
    setActiveId(active.id as number);
  }
  
  // Handler pre koniec drag
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const taskId = active.id as number;
      let targetColumnDay: number;
      
      // Skontrolujeme, či cieľom je stĺpec alebo úloha
      const overId = over.id.toString();
      
      if (overId.startsWith('column-')) {
        // Ak cieľom je stĺpec, extrahujeme číslo stĺpca
        targetColumnDay = parseInt(overId.split('-')[1]);
      } else {
        // Ak cieľom je úloha, nájdeme jej deň zo stavu
        const targetTaskId = parseInt(overId);
        const targetTask = weeklyTasks.find(task => task.id === targetTaskId);
        if (!targetTask) {
          // Target task not found during drag operation
          setActiveId(null);
          return;
        }
        targetColumnDay = targetTask.day;
      }
      
      // Aktualizujeme deň úlohy
      const newTaskDays = { ...taskDays, [taskId]: targetColumnDay };
      setTaskDays(newTaskDays);
      
      // Aktualizujeme zoznam úloh s dňami
      setWeeklyTasks(
        weeklyTasks.map(task => 
          task.id === taskId ? { ...task, day: targetColumnDay } : task
        )
      );
      
      // Uložíme aktualizované rozloženie do localStorage
      localStorage.setItem('taskCalendarLayout', JSON.stringify(newTaskDays));
    }
    
    setActiveId(null);
  }
  
  // Aktívna úloha počas drag-and-drop
  const activeTask = activeId ? weeklyTasks.find(task => task.id === activeId) : null;
  
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToWindowEdges]}
    >
      <div className="w-full">
        <h3 className="text-lg font-semibold mb-4">Plánovač týždňa</h3>
        {/* Mobile: horizontal scroll, Desktop: grid */}
        <div className="block lg:hidden">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {weekDays.map((day, dayIndex) => {
              const dayTasks = weeklyTasks.filter(task => task.day === dayIndex);
              
              return (
                <div key={`mobile-day-${dayIndex}`} className="flex-shrink-0 w-64">
                  <div className="text-center text-sm font-medium pb-2 mb-2">
                    <div className={cn(
                      day.isToday && "text-primary font-semibold"
                    )}>
                      {day.label}
                    </div>
                    <div className="text-xs text-muted-foreground">{day.dateString}</div>
                  </div>
                  <CalendarDayColumn 
                    id={`column-${dayIndex}`}
                    isToday={day.isToday}
                    tasks={dayTasks}
                    onTaskClick={onTaskClick}
                  />
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Desktop: grid layout */}
        <div className="hidden lg:grid grid-cols-7 gap-2">
          {weekDays.map((day, index) => (
            <div 
              key={index} 
              className={cn(
                "text-center text-sm font-medium pb-1",
                day.isToday && "text-primary"
              )}
            >
              <div>{day.label}</div>
              <div className="text-xs text-muted-foreground">{day.dateString}</div>
            </div>
          ))}
          
          {weekDays.map((day, dayIndex) => {
            const dayTasks = weeklyTasks.filter(task => task.day === dayIndex);
            
            return (
              <CalendarDayColumn 
                key={`day-${dayIndex}`} 
                id={`column-${dayIndex}`}
                isToday={day.isToday}
                tasks={dayTasks}
                onTaskClick={onTaskClick}
              />
            );
          })}
        </div>
      </div>
      
      {/* Overlay pre drag-and-drop */}
      <DragOverlay adjustScale zIndex={999}>
        {activeId && activeTask ? (
          <div className="opacity-80 w-[16rem]">
            <TaskCard task={activeTask} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}